import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Database, Calendar, MessageCircle, CheckCircle2, RefreshCw, BarChart3, Shield, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "../components/bits";
import { FAQS } from "../data/content";

const ICONS = {
    database: Database,
    calendar: Calendar,
    message: MessageCircle,
    check: CheckCircle2,
    refresh: RefreshCw,
    analytics: BarChart3,
    shield: Shield,
};

export const FaqAccordion = ({ dark = false, idPrefix = "faq" }) => {
    const [open, setOpen] = useState(0);
    return (
        <div className={`divide-y ${dark ? "divide-cream/10" : "divide-linen"}`} data-testid={`${idPrefix}-accordion`}>
            {FAQS.map((f, i) => {
                const Icon = ICONS[f.icon];
                const isOpen = open === i;
                return (
                    <div key={f.q}>
                        <button
                            data-testid={`${idPrefix}-question-${i + 1}`}
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            aria-expanded={isOpen}
                            className="group flex w-full items-center gap-4 py-5 text-left sm:gap-6"
                        >
                            <span
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                                    isOpen
                                        ? "border-gold/60 bg-blush"
                                        : dark
                                          ? "border-cream/15 group-hover:border-gold/40"
                                          : "border-linen group-hover:border-gold/50"
                                }`}
                            >
                                <Icon className={`h-4 w-4 ${isOpen ? "text-ink" : dark ? "text-gold" : "text-ink/60"}`} strokeWidth={1.5} />
                            </span>
                            <span
                                className={`flex-1 font-serif text-base uppercase leading-snug tracking-tight transition-colors duration-300 sm:text-lg ${
                                    dark ? "text-cream" : "text-ink"
                                } ${isOpen ? "" : "opacity-80 group-hover:opacity-100"}`}
                            >
                                {f.q}
                            </span>
                            <motion.span
                                animate={{ rotate: isOpen ? 45 : 0 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                                    dark ? "border-cream/15 text-cream/70" : "border-linen text-ink/60"
                                }`}
                            >
                                <Plus className="h-4 w-4" strokeWidth={1.5} />
                            </motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                    className="overflow-hidden"
                                >
                                    <p
                                        className={`pb-6 pl-[52px] pr-4 text-sm leading-relaxed sm:pl-[60px] sm:text-base ${
                                            dark ? "text-cream/65" : "text-mute"
                                        }`}
                                        data-testid={`${idPrefix}-answer-${i + 1}`}
                                    >
                                        {f.a}
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
};

export const FaqPreview = () => (
    <section id="faq" data-testid="faq-preview" className="bg-sand/60 py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[1fr_1.5fr] lg:gap-20 lg:px-8">
            <div>
                <SectionHead
                    eyebrow="Questions"
                    title="FAQ"
                    copy="Straight answers about your database, your booking software, your tone of voice and how results are tracked."
                />
                <Reveal delay={0.2}>
                    <Link
                        data-testid="faq-view-all-btn"
                        to="/faq"
                        className="btn-arrow group mt-8 inline-flex items-center gap-3 rounded-full border border-ink/20 px-6 py-3 font-sans text-[12px] font-medium uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-cream"
                    >
                        Open the full FAQ page
                        <ArrowRight className="btn-arrow-icon h-4 w-4" strokeWidth={1.75} />
                    </Link>
                </Reveal>
            </div>
            <Reveal delay={0.1}>
                <div className="rounded-3xl border border-linen bg-ivory px-6 py-2 sm:px-8">
                    <FaqAccordion idPrefix="home-faq" />
                </div>
            </Reveal>
        </div>
    </section>
);
